import { useState } from 'react';
import { Activity } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { EmptyState } from '../atoms/EmptyState';
import { Inline } from '../atoms/Inline/Inline';
import { Text } from '../atoms/Typography/Text';
import { Section } from '../templates/Section/Section';
import { TableWrap } from '../budget/BudgetPageParts';
import { useToast } from '../../stores/toastStore';
import type { SensorMapping } from '../../api/controller';
import { useUpdateSensorMapping } from '../../hooks/useController';
import { formatDateTime, formatValue, isStale, mappingName } from './controllerFormat';
import { SensorMappingDialog } from './SensorMappingDialog';
import { SensorHistoryDialog } from './SensorHistoryDialog';
import './Controller.css';

function Meaning({ mapping }: { mapping: SensorMapping }) {
  const { t } = useTranslation();
  if (mapping.kind === 'unmapped') return <Badge tone="warning">{t('controller.kind.unmapped')}</Badge>;
  if (mapping.kind === 'parameter' && mapping.parameterId) {
    const parameter = t(`controller.parameter.${mapping.parameterId}`);
    return (
      <>
        {mapping.location === 'tank' ? parameter : `${parameter} (${t(`controller.location.${mapping.location}`)})`}
        {mapping.recordAsMeasurement && ` · ${t('controller.keys.asMeasurement')}`}
      </>
    );
  }
  return (
    <>
      {t(`controller.kind.${mapping.kind}`)}
      {mapping.countsTowardCost && ` · ${t('controller.keys.countsTowardCost')}`}
    </>
  );
}

export interface SensorMappingsProps {
  aquariumId: string;
  mappings: readonly SensorMapping[];
}

/** Every key a bridge reported, its meaning and latest value; edit the mapping or open the history. */
export function SensorMappings({ aquariumId, mappings }: SensorMappingsProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const { showToast } = useToast();
  const update = useUpdateSensorMapping(aquariumId);
  const [editing, setEditing] = useState<SensorMapping | null>(null);
  const [history, setHistory] = useState<SensorMapping | null>(null);
  const unmapped = mappings.filter((m) => m.kind === 'unmapped').length;
  const caption = t('controller.keys.caption');

  return (
    <Section
      title={t('controller.keys.title')}
      description={unmapped > 0 ? t('controller.keys.descriptionUnmapped', { count: unmapped }) : t('controller.keys.description')}
    >
      <Card>
        {mappings.length === 0 ? (
          <EmptyState
            icon={<Activity aria-hidden="true" />}
            headline={t('controller.keys.emptyTitle')}
            description={t('controller.keys.emptyDescription')}
            headingLevel={3}
          />
        ) : (
          <TableWrap label={caption}>
            <table className="controller-table" data-testid="controller-keys-table">
              <caption className="controller-table__caption">{caption}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('controller.keys.name')}</th>
                  <th scope="col">{t('controller.keys.meaning')}</th>
                  <th scope="col">{t('controller.keys.origin')}</th>
                  <th scope="col" className="controller-table__num">{t('controller.keys.latest')}</th>
                  <th scope="col">{t('controller.keys.received')}</th>
                  <th scope="col">
                    <span className="controller-table__sr">{t('controller.keys.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping) => {
                  const name = mappingName(mapping);
                  const ignored = mapping.kind === 'ignore';
                  return (
                    <tr key={mapping.id} className={ignored ? 'controller-table__row--ignored' : undefined}>
                      <th scope="row">
                        <span className="controller-table__name">{name}</span>
                        {mapping.label && <Text as="span" color="muted" size="sm" className="controller-table__key">{mapping.sourceKey}</Text>}
                      </th>
                      <td><Meaning mapping={mapping} /></td>
                      <td>{mapping.origin ?? '–'}</td>
                      <td className="controller-table__num">
                        {mapping.lastValue !== null ? formatValue(mapping.lastValue, mapping.unit, language) : '–'}
                      </td>
                      <td>
                        {mapping.lastMeasuredAt ? (
                          <>
                            <time dateTime={mapping.lastMeasuredAt}>{formatDateTime(mapping.lastMeasuredAt, language)}</time>
                            {isStale(mapping.lastMeasuredAt) && !ignored && (
                              <>
                                {' '}
                                <Badge tone="warning">{t('controller.keys.stale')}</Badge>
                              </>
                            )}
                          </>
                        ) : (
                          '–'
                        )}
                      </td>
                      <td>
                        <Inline gap="2" wrap={false}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setHistory(mapping)}
                            disabled={ignored}
                            aria-label={t('controller.keys.historyFor', { name })}
                          >
                            {t('controller.keys.history')}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              update.reset();
                              setEditing(mapping);
                            }}
                            aria-label={t('controller.keys.editFor', { name })}
                          >
                            {t('controller.keys.edit')}
                          </Button>
                        </Inline>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      {editing && (
        <SensorMappingDialog
          mapping={editing}
          onCancel={() => setEditing(null)}
          isSubmitting={update.isPending}
          submitError={update.isError}
          onSubmit={(input) =>
            update.mutate(
              { id: editing.id, input },
              {
                onSuccess: () => {
                  setEditing(null);
                  showToast(t('controller.mapping.saved'), 'success');
                },
              },
            )
          }
        />
      )}
      {history && <SensorHistoryDialog aquariumId={aquariumId} mapping={history} onClose={() => setHistory(null)} />}
    </Section>
  );
}
